import DOMPurify from 'dompurify';

/**
 * sanitizeBlogBody — allowlist-sanitize author-authored blog-post HTML before it is
 * injected via `dangerouslySetInnerHTML` (pages/BlogPostPage.tsx). Item 57 Layer 1.
 *
 * Uses plain `dompurify` (browser DOM), deliberately NOT isomorphic-dompurify:
 *   - Blog bodies are fetched CLIENT-SIDE (getPostBySlug in a useEffect), so the body is
 *     never rendered during the vite-react-ssg Node bake (the SSG render is the loading
 *     state). There is no SSG-baked body to keep in parity, so a jsdom-backed sanitizer
 *     buys nothing — sanitization only ever runs in the browser, where a real DOM exists.
 *   - dompurify imports cleanly in Node (no jsdom), so importing this module during the
 *     SSG render of BlogPostPage does NOT crash the build. (isomorphic-dompurify@2/3
 *     import-crash on the project's Node via jsdom's ESM-only transitive deps.)
 *
 * Pure + dependency-light (mirrors lib/readTime.ts / lib/relatedPosts.ts). Unit-tested
 * under the jsdom env (a real `window` is present there, so dompurify sanitizes).
 *
 * Allowlist is sized to the current seed corpus (<p>/<h2>/<a> only) PLUS reasonable
 * editorial headroom (h2–h4, lists, inline emphasis, links, blockquote, code/pre, hr, br).
 * Everything else — <script>/<iframe>/<object>/<svg>, on*= handlers, javascript:/data:
 * URLs — is stripped. It is a no-op on the current corpus (proven in the test).
 */

// Block elements + inline formatting an editorial blog body legitimately needs. No <h1>
// (the page renders the post title as the sole h1), no <img> (body images aren't used —
// the cover image is a separate field), no <table>/<style>/media — add later if needed.
// Exported so the Node-side write-side validator's mirror (scripts/blog-tag-vocab.mjs) can be
// drift-guarded set-equal to it (scripts/blog-tag-vocab.test.ts) — a single allowlist contract.
export const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'h2', 'h3', 'h4',
  'ul', 'ol', 'li',
  'strong', 'em', 'b', 'i', 'u', 's',
  'a',
  'blockquote',
  'code', 'pre',
];

// Only the attributes <a> needs. No class/style/id/data-* (the parent `.prose` owns styling).
export const ALLOWED_ATTR = ['href', 'target', 'rel'];

// http / https / mailto / relative (path, anchor, query) ONLY — blocks javascript:, data:,
// vbscript:, tel:, ftp:, etc. Same shape as DOMPurify's default safe-URI regexp, narrowed
// to the three schemes a blog body should ever link with.
// Exported (UI-06) so the BUILD-side sanitizer in vite.config.ts uses the identical
// URI policy — one contract, two runtimes, no drift.
export const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

// Reverse-tabnabbing guard: force rel="noopener noreferrer" on any target="_blank" link.
// Registered once on the shared DOMPurify singleton (this is the app's only sanitizer).
// Guarded so a Node/SSG import (no DOM) can't crash on a missing method.
if (typeof DOMPurify.addHook === 'function') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

export const sanitizeBlogBody = (html: string): string => {
  if (!html) return '';
  // UI-06: Node/SSG pass-through — in Node plain dompurify has NO .sanitize
  // (the default export is a window-taking factory; isSupported === false), so
  // calling it here would CRASH the vite-react-ssg render. This module's
  // original assumption ("the body is never rendered during the Node bake") no
  // longer holds: blog-post routes now bake their bodies. The invariant that
  // replaces it: EVERY body reaching a Node render arrives ALREADY SANITIZED by
  // the build (vite.config.ts sanitizes with jsdom-backed DOMPurify using the
  // exact ALLOWED_* exports above, at payload construction).
  //
  // ⚠️ REVIEW-CORRECTED SECURITY MODEL — do not soften this: the browser-side
  // sanitize below does NOT re-protect the hydrated first paint. React 19
  // production hydration RETAINS the server-baked innerHTML verbatim (no prop
  // diffing), and subsequent renders diff the memo-stable sanitized string
  // against itself — so what a first-paint visitor's browser executes is
  // exactly the jsdom-bake output re-parsed by the BROWSER parser (the classic
  // cross-parser/mXSS configuration). The baked DOM's safety therefore rests
  // SOLELY on the build-time jsdom sanitize PLUS this allowlist's narrowness.
  // The browser-side call protects only client-side-navigation fetches and dev.
  // CONSEQUENCE: widening ALLOWED_TAGS with namespace-bearing or
  // structure-sensitive tags (svg, math, style, template, noscript, table,
  // form, img) is NOT a routine edit — it reopens the mXSS surface with no
  // browser backstop on the baked path. Treat any widening as a security
  // change requiring its own review.
  if (typeof DOMPurify.sanitize !== 'function') return html;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  });
};
